import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import clinicAPI from "../../../services/clinicApi";
import {
  formatDateRu,
  formatTimeRu,
  toDateInputValue,
} from "../../../utils/appointmentLabels";
import L from "../../../constants/baysportLabels";
import s from "../clinic.module.css";
import aa from "../AdminPortal/adminApp.module.css";
import md from "../common/manageDesktop.module.css";
import { HubScreen, HubFilterChips, HubTabScreen } from "../AdminPortal/HubPwa";
import { useAdminHubUi } from "../../../hooks/useAdminAppMode";
import { useAdminRefresh } from "../../../hooks/useAdminRefresh";
import {
  Button,
  Card,
  Field,
  Input,
  Select,
  EmptyState,
  useToast,
} from "../../ui";
import { useHubBasePath, useHubScheduleUrl, hubClientsPath } from "../../../utils/hubPaths";
import AttendanceAbsenceSheet from "../AdminSchedule/AttendanceAbsenceSheet";
import CompetitionRegistrationsPanel from "../AdminSchedule/CompetitionRegistrationsPanel";
import JournalOfflineBanner from "./JournalOfflineBanner";
import { useJournalOffline } from "../../../hooks/useJournalOffline";
import {
  applyLocalAttendanceMark,
  cacheJournal,
  cacheSessions,
  enqueueMutation,
  readCachedJournal,
  readCachedSessions,
} from "../../../utils/journalOfflineStore";
import {
  attendanceMarkPayload,
  attendanceMarkState,
  attendanceStatusShort,
  canMarkSessionAttendance,
  rollCallHeroLine,
  rollCallStatsFromAttendances,
  sessionLabel,
  sessionRollCallStats,
} from "../AdminSchedule/calendarUtils";

function memberInitials(name) {
  return String(name || "?")
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function visitsLabel(att) {
  const m = att.membership;
  if (!m) return "нет абонемента";
  if (m.status === "pending") {
    const name = m.plan?.name || "абонемент";
    return `${name} · не оплачен`;
  }
  if (m.plan?.plan_type === "visits" && m.visits_remaining != null) {
    return `${m.visits_remaining} зан.`;
  }
  if (m.ends_at) return `до ${m.ends_at}`;
  return "безлимит";
}

function membershipWarnLabel(warn) {
  if (warn === "unpaid") return "Должник";
  if (warn === "low_visits") return "Мало занятий";
  if (warn === "expired") return "Абонемент истёк";
  if (warn === "none") return "Нет абонемента";
  return null;
}

const AVATAR_TONES = [
  "#e0e7ff",
  "#dbeafe",
  "#dcfce7",
  "#fef3c7",
  "#fce7f3",
  "#ede9fe",
];

function avatarTone(id) {
  const s = String(id || "0");
  let n = 0;
  for (let i = 0; i < s.length; i += 1) n += s.charCodeAt(i);
  return AVATAR_TONES[n % AVATAR_TONES.length];
}

function shiftDateInput(dateStr, days) {
  const d = new Date(`${dateStr}T12:00:00`);
  d.setDate(d.getDate() + days);
  return toDateInputValue(d);
}

function weekDatesForAnchor(dateStr) {
  const anchor = new Date(`${dateStr}T12:00:00`);
  const dow = anchor.getDay();
  const monday = new Date(anchor);
  monday.setDate(anchor.getDate() + (dow === 0 ? -6 : 1 - dow));
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return toDateInputValue(d);
  });
}

function formatJournalHeader(dateStr) {
  const d = new Date(`${dateStr}T12:00:00`);
  return {
    title: d.toLocaleDateString("ru-RU", { day: "numeric", month: "long" }),
    meta: `${d.getFullYear()} · ${d.toLocaleDateString("ru-RU", {
      weekday: "long",
    })}`,
  };
}

function JournalNavChevron({ direction }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d={direction === "left" ? "M14.5 6.5L9 12l5.5 5.5" : "M9.5 6.5L15 12l-5.5 5.5"}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function weekMarkersFromSessions(sessions) {
  const has = new Set();
  const needs = new Set();
  for (const sess of sessions) {
    if (sess.status === "cancelled") continue;
    if (sess.session_kind === "event") continue;
    const dayIso = (sess.start_datetime || "").slice(0, 10);
    if (!dayIso) continue;
    has.add(dayIso);
    if (sess.needs_attendance) needs.add(dayIso);
  }
  return { has, needs };
}

function JournalSessionCard({ sess, onOpen, isLast }) {
  const { total, present, marked, allMarked, allPresent } =
    sessionRollCallStats(sess);
  const needs = Boolean(sess.needs_attendance);

  const statusClass = allPresent
    ? aa.journalSessionBadgeDone
    : allMarked
      ? aa.journalSessionBadgeMuted
      : needs
        ? aa.journalSessionBadgeWarn
        : marked > 0
          ? aa.journalSessionBadgeMuted
          : aa.journalSessionBadgeEmpty;

  return (
    <button
      type="button"
      className={`${aa.journalSessionCard} ${needs ? aa.journalSessionCardNeeds : ""} ${isLast ? aa.journalSessionCardLast : ""}`}
      onClick={onOpen}
    >
      <div className={aa.journalSessionTimeCol}>
        <span>{formatTimeRu(sess.start_datetime)}</span>
        <span className={aa.journalSessionTimeEnd}>
          {formatTimeRu(sess.end_datetime)}
        </span>
      </div>
      <div className={aa.journalSessionBody}>
        <span className={aa.journalSessionTitle}>
          {sessionLabel(sess)}
        </span>
        <span className={aa.journalSessionMeta}>
          {[
            sess.branch?.name,
            sess.trainer?.user?.full_name || sess.trainer?.full_name,
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </div>
      <span
        className={`${aa.journalSessionBadge} ${statusClass}`}
        title={
          total
            ? `Отмечено ${marked}, было ${present} из ${total}`
            : undefined
        }
      >
        {total ? `${marked}/${total}` : "—"}
      </span>
      <span className={aa.journalSessionChevron} aria-hidden />
    </button>
  );
}

function JournalCalendarIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M8 3v2M16 3v2M5 9h14M6 5h12a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function JournalWeekPicker({ date, onPick, daysWithSessions, daysNeedingAttendance }) {
  const today = toDateInputValue();
  const week = weekDatesForAnchor(date);
  const header = formatJournalHeader(date);
  const prevWeek = shiftDateInput(date, -7);
  const nextWeek = shiftDateInput(date, 7);

  return (
    <div className={aa.journalDateSection}>
      <div className={aa.journalDateHead}>
        <div className={aa.journalDateHeadText}>
          <h2 className={aa.journalMonthTitle}>{header.title}</h2>
          <p className={aa.journalMonthMeta}>{header.meta}</p>
        </div>
        <div className={aa.journalDateHeadActions}>
          {date !== today ? (
            <button
              type="button"
              className={aa.journalTodayLink}
              onClick={() => onPick(today)}
            >
              Сегодня
            </button>
          ) : null}
          <label className={aa.journalDateCalendarBtn} aria-label="Выбрать дату">
            <JournalCalendarIcon />
            <input
              type="date"
              value={date}
              onChange={(e) => onPick(e.target.value)}
            />
          </label>
        </div>
      </div>

      <div className={aa.journalWeekRow}>
        <button
          type="button"
          className={aa.journalWeekNav}
          onClick={() => onPick(prevWeek)}
          aria-label="Предыдущая неделя"
        >
          <JournalNavChevron direction="left" />
        </button>
        <div className={aa.journalWeekStrip} role="group" aria-label="Дни недели">
          {week.map((dayIso) => {
            const d = new Date(`${dayIso}T12:00:00`);
            const active = dayIso === date;
            const isToday = dayIso === today;
            const wd = d
              .toLocaleDateString("ru-RU", { weekday: "short" })
              .replace(/\./g, "")
              .slice(0, 2);
            const hasSessions = daysWithSessions?.has(dayIso);
            const needsMark = daysNeedingAttendance?.has(dayIso);
            let dotClass = aa.journalDayDot;
            if (active) dotClass = aa.journalDayDotOn;
            else if (needsMark) dotClass = aa.journalDayDotWarn;
            else if (hasSessions) dotClass = aa.journalDayDotHas;
            return (
              <button
                key={dayIso}
                type="button"
                className={
                  active ? `${aa.journalDay} ${aa.journalDayOn}` : aa.journalDay
                }
                onClick={() => onPick(dayIso)}
                aria-pressed={active}
                aria-label={formatDateRu(`${dayIso}T12:00:00`, {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                })}
              >
                <span className={aa.journalDayWd}>{wd}</span>
                <span className={aa.journalDayNum}>{d.getDate()}</span>
                {hasSessions || (isToday && !active) ? (
                  <span className={dotClass} aria-hidden />
                ) : null}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className={aa.journalWeekNav}
          onClick={() => onPick(nextWeek)}
          aria-label="Следующая неделя"
        >
          <JournalNavChevron direction="right" />
        </button>
      </div>
    </div>
  );
}

function JournalCompetitionView({ journal }) {
  const summary = journal.competition_registration_summary || {};
  const approved = summary.approved ?? 0;
  const pending = summary.pending ?? 0;
  const rejected = summary.rejected ?? 0;

  return (
    <div className={aa.journalAttendPage}>
      <div className={aa.journalCompetitionHeroCard}>
        <span className={aa.journalCompetitionBadge}>Соревнование</span>
        <h2 className={aa.journalCompetitionTitle}>
          {journal.title || "Соревнование"}
        </h2>
        <p className={aa.journalCompetitionTime}>
          {formatTimeRu(journal.start_datetime)} —{" "}
          {formatTimeRu(journal.end_datetime)}
        </p>
        <p className={aa.journalCompetitionMeta}>
          {[
            journal.branch?.name,
            journal.trainer?.user?.full_name || journal.trainer?.full_name,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>

      <div className={aa.journalCompetitionStats}>
        <div
          className={`${aa.journalCompetitionStat} ${aa.journalCompetitionStatApproved}`}
        >
          <strong>{approved}</strong>
          <span>допущено</span>
        </div>
        <div
          className={`${aa.journalCompetitionStat} ${aa.journalCompetitionStatPending}`}
        >
          <strong>{pending}</strong>
          <span>на рассмотрении</span>
        </div>
        <div
          className={`${aa.journalCompetitionStat} ${aa.journalCompetitionStatRejected}`}
        >
          <strong>{rejected}</strong>
          <span>не допущен</span>
        </div>
      </div>

      <div className={aa.journalCompetitionBody}>
        <CompetitionRegistrationsPanel sessionId={journal.id} variant="sheet" />
      </div>
    </div>
  );
}

function JournalAttendanceView({
  journal,
  mark,
  markDisabled,
  onAbsence,
  onMarkAll,
  markingAll,
  markingPatientId,
}) {
  const hubBase = useHubBasePath();
  const touchStart = useRef({ x: 0, y: 0, id: null });

  if (!journal) return null;

  const isCompetition =
    journal.session_kind === "event" && journal.event_kind === "competition";

  if (isCompetition) {
    return <JournalCompetitionView journal={journal} />;
  }

  const attendances = journal.attendances || [];
  const roll = rollCallStatsFromAttendances(attendances, journal);
  const present = roll.present;
  const marked = roll.marked;
  const expected = roll.total || attendances.length;
  const heroLine = rollCallHeroLine(roll);
  const canMark = !markDisabled;
  const rowBusy = (id) => markingAll || markingPatientId === id;

  return (
    <div className={aa.journalAttendPage}>
      <div className={aa.journalAttendHero}>
        <p className={aa.journalAttendHeroTime}>
          {formatTimeRu(journal.start_datetime)} —{" "}
          {formatTimeRu(journal.end_datetime)}
        </p>
        <p className={aa.journalAttendHeroMeta}>
          {[
            journal.branch?.name,
            journal.trainer?.user?.full_name || journal.trainer?.full_name,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
        {expected > 0 ? (
          <p className={aa.journalAttendHeroStat}>
            Перекличка {marked}/{expected}
            {present > 0 ? ` · было ${present}` : marked === 0 ? " · нет отметок" : ""}
          </p>
        ) : null}
      </div>

      {canMark ? (
        <div className={aa.journalAttendToolbar}>
          <details className={aa.journalAttendHelp}>
            <summary>Подсказка</summary>
            <p>
              «Был», «Не был» (с причиной), «Предупредил» — все считаются в
              перекличке. Повторный тап снимает отметку и возвращает занятие на
              абонемент.
            </p>
          </details>
          {expected > 0 && marked < expected ? (
            <button
              type="button"
              className={aa.journalMarkAllBtn}
              disabled={markingAll}
              onClick={onMarkAll}
            >
              {markingAll ? "…" : "Все «Был»"}
            </button>
          ) : null}
        </div>
      ) : (
        <p className={aa.journalAttendCancelled}>Занятие отменено — отметка недоступна</p>
      )}

      {attendances.length === 0 ? (
        <EmptyState
          title="В группе никого нет"
          description="Добавьте участников в карточке группы"
        />
      ) : (
        <ul className={aa.journalAttendList}>
          {attendances.map((a) => {
            const { marked, isPresent, isLate, isExcused, isAbsentOnly } =
              attendanceMarkState(a);
            const warnText = membershipWarnLabel(a.membership_warning);
            const hasError = isPresent && a.charge_error;

            return (
              <li
                key={a.id}
                className={
                  hasError
                    ? `${aa.journalAttendRow} ${aa.journalAttendRowError}`
                    : !marked
                      ? `${aa.journalAttendRow} ${aa.journalAttendRowUnmarked}`
                      : aa.journalAttendRow
                }
                onTouchStart={(e) => {
                  if (!canMark) return;
                  const t = e.touches[0];
                  touchStart.current = {
                    x: t.clientX,
                    y: t.clientY,
                    id: a.patient_id,
                  };
                }}
                onTouchEnd={(e) => {
                  if (!canMark) return;
                  const t = e.changedTouches[0];
                  const dx = t.clientX - touchStart.current.x;
                  if (touchStart.current.id !== a.patient_id) return;
                  if (
                    dx < -60 &&
                    Math.abs(t.clientY - touchStart.current.y) < 40
                  ) {
                    onAbsence({
                      patientId: a.patient_id,
                      name: a.patient?.full_name,
                    });
                    return;
                  }
                }}
              >
                <div
                  className={aa.journalAttendAvatar}
                  style={{ background: avatarTone(a.patient_id) }}
                  aria-hidden
                >
                  {memberInitials(a.patient?.full_name)}
                </div>
                <div className={aa.journalAttendInfo}>
                  <div className={aa.journalAttendNameRow}>
                    <Link to={hubClientsPath(hubBase, a.patient_id)}>
                      {a.patient?.full_name || "—"}
                    </Link>
                  </div>
                  <span className={aa.journalAttendMeta}>
                    {attendanceStatusShort(a)}
                    {marked ? ` · ${visitsLabel(a)}` : ""}
                    {marked && hasError ? ` · ${a.charge_error}` : ""}
                  </span>
                  {warnText ? (
                    <span
                      className={
                        a.membership_warning === "unpaid"
                          ? `${aa.journalAttendWarn} ${aa.journalAttendWarnUnpaid}`
                          : aa.journalAttendWarn
                      }
                    >
                      {warnText}
                    </span>
                  ) : null}
                  {a.medical_warning === "expired" ? (
                    <span className={aa.journalAttendWarn}>Допуск просрочен</span>
                  ) : null}
                  {isLate ? (
                    <span className={aa.journalAttendExcused}>Опоздал</span>
                  ) : null}
                  {isExcused ? (
                    <span className={aa.journalAttendExcused}>Предупредил</span>
                  ) : null}
                </div>
                <div
                  className={aa.journalAttendToggle}
                  role="group"
                  aria-label="Посещаемость"
                >
                  <button
                    type="button"
                    className={`${aa.journalAttendToggleBtn} ${aa.journalAttendTogglePresent} ${
                      isPresent && !isLate ? aa.journalAttendToggleOn : ""
                    }`}
                    disabled={!canMark || rowBusy(a.patient_id)}
                    onClick={(e) => {
                      e.stopPropagation();
                      mark(a.patient_id, "present", undefined, a);
                    }}
                    onTouchStart={(e) => e.stopPropagation()}
                    aria-pressed={isPresent}
                    aria-label="Был"
                  >
                    ✓
                  </button>
                  <button
                    type="button"
                    className={`${aa.journalAttendToggleBtn} ${
                      isLate ? aa.journalAttendToggleOn : ""
                    }`}
                    disabled={!canMark || rowBusy(a.patient_id)}
                    onClick={(e) => {
                      e.stopPropagation();
                      mark(a.patient_id, "late", undefined, a);
                    }}
                    onTouchStart={(e) => e.stopPropagation()}
                    aria-pressed={isLate}
                    aria-label="Опоздал"
                  >
                    ⏱
                  </button>
                  <button
                    type="button"
                    className={`${aa.journalAttendToggleBtn} ${aa.journalAttendToggleAbsent} ${
                      isAbsentOnly ? aa.journalAttendToggleOn : ""
                    }`}
                    disabled={!canMark || rowBusy(a.patient_id)}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isAbsentOnly) {
                        mark(a.patient_id, "absent", undefined, a);
                        return;
                      }
                      onAbsence({
                        patientId: a.patient_id,
                        name: a.patient?.full_name,
                      });
                    }}
                    onTouchStart={(e) => e.stopPropagation()}
                    aria-pressed={isAbsentOnly}
                    aria-label="Не был"
                  >
                    ✕
                  </button>
                  <button
                    type="button"
                    className={`${aa.journalAttendToggleBtn} ${aa.journalAttendToggleExcused} ${
                      isExcused ? aa.journalAttendToggleOn : ""
                    }`}
                    disabled={!canMark || rowBusy(a.patient_id)}
                    onClick={(e) => {
                      e.stopPropagation();
                      mark(a.patient_id, "excused", undefined, a);
                    }}
                    onTouchStart={(e) => e.stopPropagation()}
                    aria-pressed={isExcused}
                    aria-label="Предупредил"
                  >
                    !
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function AttendanceRoster({
  journal,
  mark,
  markDisabled,
  onAbsence,
}) {
  const hubBase = useHubBasePath();
  if (!journal) return null;

  return (
    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
      {(journal.attendances || []).map((a) => {
        const warn = a.membership_warning;
        const state = attendanceMarkState(a);
        const hasError = state.isPresent && a.charge_error;

        return (
          <li
            key={a.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "12px 0",
              borderBottom: "1px solid var(--color-border)",
              background: hasError ? "var(--color-danger-soft)" : undefined,
            }}
          >
            <div
              className={s.userAvatar}
              style={{ width: 40, height: 40, flexShrink: 0 }}
              aria-hidden
            >
              {memberInitials(a.patient?.full_name)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Link to={hubClientsPath(hubBase, a.patient_id)}>
                  <strong>{a.patient?.full_name}</strong>
                </Link>
                {warn === "unpaid" ? <span title="Должник">🟠</span> : null}
                {warn === "low_visits" ? <span title="Мало занятий">🟡</span> : null}
                {warn === "expired" || warn === "none" ? (
                  <span title="Нет абонемента">🔴</span>
                ) : null}
              </div>
              <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
                {visitsLabel(a)}
                {!state.marked ? " · не отмечен" : ""}
                {hasError ? ` · ${a.charge_error}` : ""}
              </div>
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              <Button
                type="button"
                size="sm"
                variant={state.isPresent && !state.isLate ? "primary" : "secondary"}
                disabled={markDisabled}
                onClick={() => mark(a.patient_id, "present", undefined, a)}
              >
                ✓
              </Button>
              <Button
                type="button"
                size="sm"
                variant={state.isLate ? "primary" : "secondary"}
                disabled={markDisabled}
                onClick={() => mark(a.patient_id, "late", undefined, a)}
              >
                Опозд.
              </Button>
              <Button
                type="button"
                size="sm"
                variant={
                  state.isAbsentOnly || state.isExcused ? "primary" : "secondary"
                }
                disabled={markDisabled}
                onClick={() => {
                  if (state.isAbsentOnly) {
                    mark(a.patient_id, "absent", undefined, a);
                    return;
                  }
                  onAbsence({
                    patientId: a.patient_id,
                    name: a.patient?.full_name,
                  });
                }}
              >
                ✕
              </Button>
              <Button
                type="button"
                size="sm"
                variant={state.isExcused ? "primary" : "secondary"}
                disabled={markDisabled}
                onClick={() => mark(a.patient_id, "excused", undefined, a)}
              >
                Предупр.
              </Button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export default function AdminJournal({ staffMode = false }) {
  const toast = useToast();
  const navigate = useNavigate();
  const hubBase = useHubBasePath();
  const scheduleUrl = useHubScheduleUrl();
  const isApp = useAdminHubUi();
  const trainerProfile = useSelector((state) => state.auth.trainerProfile);
  const tenantKey = useSelector((state) => state.auth.tenant?.id || "default");
  const {
    isOnline,
    pendingCount,
    isSyncing,
    lastSyncError,
    refreshPending,
    syncNow,
  } = useJournalOffline(tenantKey);
  const [searchParams, setSearchParams] = useSearchParams();
  const date = searchParams.get("date") || toDateInputValue();
  const sessionFromUrl = searchParams.get("session") || "";
  const groupFromUrl = searchParams.get("group") || "";

  const [sessions, setSessions] = useState([]);
  const [weekDayMarkers, setWeekDayMarkers] = useState({
    has: new Set(),
    needs: new Set(),
  });
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSessionId, setActiveSessionId] = useState(sessionFromUrl);
  const [groupFilter, setGroupFilter] = useState(groupFromUrl);
  const [journal, setJournal] = useState(null);
  const [journalLoading, setJournalLoading] = useState(false);
  const [absenceModal, setAbsenceModal] = useState(null);
  const [markingAll, setMarkingAll] = useState(false);
  const [markingPatientId, setMarkingPatientId] = useState(null);

  const setDate = (d) => {
    const next = new URLSearchParams(searchParams);
    next.set("date", d);
    setSearchParams(next, { replace: true });
  };

  const clearSession = () => {
    setActiveSessionId("");
    setJournal(null);
    const next = new URLSearchParams(searchParams);
    next.delete("session");
    setSearchParams(next, { replace: true });
  };

  const selectSession = (sessionId) => {
    setActiveSessionId(sessionId);
    const next = new URLSearchParams(searchParams);
    next.set("session", sessionId);
    setSearchParams(next, { replace: true });
  };

  const loadJournalData = useCallback(
    async (sessionId) => {
      if (!sessionId) {
        setJournal(null);
        return;
      }
      setJournalLoading(true);
      try {
        if (!navigator.onLine) {
          const cached = await readCachedJournal(tenantKey, sessionId);
          if (cached) {
            setJournal(cached);
          } else {
            toast.error("Нет кэша — откройте журнал при подключении к сети");
            setJournal(null);
          }
          return;
        }
        const data = await clinicAPI.getSessionJournal(sessionId);
        setJournal(data.session);
        await cacheJournal(tenantKey, sessionId, data.session);
      } catch (e) {
        const cached = await readCachedJournal(tenantKey, sessionId);
        if (cached) {
          setJournal(cached);
          toast.info("Показаны сохранённые данные");
        } else {
          toast.error(e.response?.data?.error || "Ошибка журнала");
          setJournal(null);
        }
      } finally {
        setJournalLoading(false);
      }
    },
    [tenantKey, toast],
  );

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const week = weekDatesForAnchor(date);
      const params = { from: week[0], to: week[6], ensure: "0" };
      if (groupFilter) params.group_id = groupFilter;
      if (staffMode && trainerProfile?.id) {
        params.trainer_id = trainerProfile.id;
      }
      if (!navigator.onLine) {
        const cached = await readCachedSessions(tenantKey, date, groupFilter);
        setSessions(cached || []);
        setWeekDayMarkers({ has: new Set(), needs: new Set() });
        return;
      }
      const data = await clinicAPI.getTrainingSessions(params);
      const all = data.sessions || [];
      const list = all.filter(
        (s) => (s.start_datetime || "").slice(0, 10) === date,
      );
      setSessions(list);
      await cacheSessions(tenantKey, date, groupFilter, list);
      setWeekDayMarkers(weekMarkersFromSessions(all));
    } catch (e) {
      const cached = await readCachedSessions(tenantKey, date, groupFilter);
      if (cached) {
        setSessions(cached);
      } else {
        toast.error(e.response?.data?.error || "Ошибка загрузки");
        setSessions([]);
      }
      setWeekDayMarkers({ has: new Set(), needs: new Set() });
    } finally {
      setLoading(false);
    }
  }, [date, groupFilter, staffMode, trainerProfile?.id, tenantKey, toast]);

  useEffect(() => {
    loadSessions();
    clinicAPI.getSpecializations().then((gr) => {
      setGroups((gr.specializations || []).filter((g) => g.is_active !== false));
    });
  }, [loadSessions]);

  useEffect(() => {
    if (sessionFromUrl) setActiveSessionId(sessionFromUrl);
  }, [sessionFromUrl]);

  useEffect(() => {
    setGroupFilter(groupFromUrl);
  }, [groupFromUrl]);

  useEffect(() => {
    loadJournalData(activeSessionId);
  }, [activeSessionId, loadJournalData]);

  const handleSync = useCallback(async () => {
    const result = await syncNow();
    if (result.synced > 0) {
      if (activeSessionId) await loadJournalData(activeSessionId);
      loadSessions();
      toast.success(`Синхронизировано: ${result.synced}`);
    }
  }, [syncNow, activeSessionId, loadJournalData, loadSessions, toast]);

  const prevPendingRef = useRef(pendingCount);
  useEffect(() => {
    if (
      prevPendingRef.current > 0 &&
      pendingCount === 0 &&
      isOnline &&
      activeSessionId
    ) {
      loadJournalData(activeSessionId);
      loadSessions();
    }
    prevPendingRef.current = pendingCount;
  }, [
    pendingCount,
    isOnline,
    activeSessionId,
    loadJournalData,
    loadSessions,
  ]);

  useAdminRefresh(() => {
    loadSessions();
    if (activeSessionId) loadJournalData(activeSessionId);
  });

  const offlineBanner = (
    <JournalOfflineBanner
      isOnline={isOnline}
      pendingCount={pendingCount}
      isSyncing={isSyncing}
      lastSyncError={lastSyncError}
      onSync={handleSync}
    />
  );

  const mark = async (patientId, status, absence_reason, attendanceRow) => {
    if (!activeSessionId) return;
    if (markingPatientId != null) return;
    if (!canMarkSessionAttendance(journal)) {
      toast.error("Занятие отменено");
      return;
    }
    const row =
      attendanceRow ||
      (journal?.attendances || []).find((a) => a.patient_id === patientId);
    const payload = row
      ? attendanceMarkPayload(row, status)
      : { status };
    const apiPayload = {
      ...payload,
      absence_reason: payload.clear ? undefined : absence_reason,
    };
    setMarkingPatientId(patientId);
    const optimistic = applyLocalAttendanceMark(
      journal,
      patientId,
      status,
      absence_reason,
      payload,
    );
    setJournal(optimistic);
    await cacheJournal(tenantKey, activeSessionId, optimistic);

    const queueAndNotify = async () => {
      await enqueueMutation(tenantKey, {
        sessionId: activeSessionId,
        patientId,
        payload: apiPayload,
      });
      await refreshPending();
      toast.success(
        navigator.onLine ? "Сохранено локально, отправляем…" : "Сохранено офлайн",
      );
    };

    try {
      if (!navigator.onLine) {
        await queueAndNotify();
        return;
      }
      const data = await clinicAPI.markSessionAttendance(activeSessionId, {
        patient_id: patientId,
        ...apiPayload,
      });
      const err = data.attendance?.charge_error;
      if (payload.clear) toast.success("Отметка снята");
      else if (payload.status === "present" && err) toast.info(err);
      else if (data.auto_completed) toast.success("Перекличка завершена");
      else toast.success("Сохранено");
      await loadJournalData(activeSessionId);
      loadSessions();
    } catch (e) {
      if (!navigator.onLine || e.code === "ERR_NETWORK") {
        await queueAndNotify();
      } else {
        toast.error(e.response?.data?.error || "Ошибка");
        loadJournalData(activeSessionId);
      }
    } finally {
      setMarkingPatientId(null);
    }
  };

  const markAllPresent = async () => {
    if (!activeSessionId || !journal) return;
    const pending = (journal.attendances || []).filter(
      (a) => !attendanceMarkState(a).isPresent,
    );
    if (!pending.length) return;
    setMarkingAll(true);
    let nextJournal = journal;
    for (const a of pending) {
      nextJournal = applyLocalAttendanceMark(
        nextJournal,
        a.patient_id,
        "present",
        undefined,
        { status: "present" },
      );
    }
    setJournal(nextJournal);
    await cacheJournal(tenantKey, activeSessionId, nextJournal);

    try {
      if (!navigator.onLine) {
        for (const a of pending) {
          await enqueueMutation(tenantKey, {
            sessionId: activeSessionId,
            patientId: a.patient_id,
            payload: { status: "present" },
          });
        }
        await refreshPending();
        toast.success("Все отмечены офлайн");
        return;
      }
      let autoCompleted = false;
      const results = await Promise.allSettled(
        pending.map((a) =>
          clinicAPI.markSessionAttendance(activeSessionId, {
            patient_id: a.patient_id,
            status: "present",
          }),
        ),
      );
      const ok = results.filter((r) => r.status === "fulfilled");
      const fail = results.filter((r) => r.status === "rejected");
      if (fail.length) {
        await loadJournalData(activeSessionId);
        loadSessions();
        if (ok.length) {
          toast.error(
            `Отмечено ${ok.length} из ${pending.length}. Повторите для остальных.`,
          );
        } else {
          const err = fail[0].reason;
          toast.error(err?.response?.data?.error || "Ошибка");
        }
        return;
      }
      autoCompleted = ok.some((r) => r.value?.auto_completed);
      await loadJournalData(activeSessionId);
      loadSessions();
      toast.success(autoCompleted ? "Перекличка завершена" : "Все отмечены");
    } catch (e) {
      if (!navigator.onLine || e.code === "ERR_NETWORK") {
        for (const a of pending) {
          await enqueueMutation(tenantKey, {
            sessionId: activeSessionId,
            patientId: a.patient_id,
            payload: { status: "present" },
          });
        }
        await refreshPending();
        toast.success("Сохранено офлайн, отправим при связи");
      } else {
        toast.error(e.response?.data?.error || "Ошибка");
        loadJournalData(activeSessionId);
      }
    } finally {
      setMarkingAll(false);
    }
  };

  const setGroupFilterAndUrl = (id) => {
    setGroupFilter(id);
    const next = new URLSearchParams(searchParams);
    if (id) next.set("group", id);
    else next.delete("group");
    setSearchParams(next, { replace: true });
  };

  const groupChips = [
    { id: "", label: "Все" },
    ...groups.map((g) => ({ id: String(g.id), label: g.name })),
  ];

  const markDisabled = !journal || !canMarkSessionAttendance(journal);

  const absenceSheet = (
    <AttendanceAbsenceSheet
      open={Boolean(absenceModal)}
      onClose={() => setAbsenceModal(null)}
      subtitle={absenceModal?.name}
      onPick={(reason) => {
        const row = (journal?.attendances || []).find(
          (x) => x.patient_id === absenceModal?.patientId,
        );
        mark(absenceModal.patientId, "absent", reason, row);
      }}
    />
  );

  if (isApp) {
    if (activeSessionId) {
      const sess =
        journal ||
        sessions.find((x) => String(x.id) === String(activeSessionId));
      const title = sess ? sessionLabel(sess) : L.journal;

      return (
        <>
          <HubScreen
            title={title}
            onBack={clearSession}
            bodyClassName={aa.journalHubBody}
          >
            {offlineBanner}
            {journalLoading && !journal ? (
              <p className={aa.journalHint}>Загрузка…</p>
            ) : !journal ? (
              <EmptyState title="Занятие не найдено" />
            ) : (
              <JournalAttendanceView
                journal={journal}
                mark={mark}
                markDisabled={markDisabled}
                onAbsence={setAbsenceModal}
                onMarkAll={markAllPresent}
                markingAll={markingAll}
                markingPatientId={markingPatientId}
              />
            )}
          </HubScreen>
          {absenceSheet}
        </>
      );
    }

    return (
      <>
        <HubTabScreen
          title={L.journal}
          bodyClassName={aa.journalHubBody}
        >
          {offlineBanner}
          <JournalWeekPicker
            date={date}
            onPick={setDate}
            daysWithSessions={weekDayMarkers.has}
            daysNeedingAttendance={weekDayMarkers.needs}
          />

          {groups.length > 0 ? (
            <div className={aa.journalFiltersWrap}>
              <HubFilterChips
                tabs={groupChips}
                active={String(groupFilter)}
                onChange={setGroupFilterAndUrl}
              />
            </div>
          ) : null}

          <div className={aa.journalSessionList}>
            {loading ? (
              <p className={aa.journalHint}>Загрузка занятий…</p>
            ) : sessions.length === 0 ? (
              <EmptyState
                title="Нет занятий на этот день"
                description="Проверьте расписание группы или выберите другую дату"
                actionLabel="Календарь"
                onAction={() => navigate(scheduleUrl({ view: "day", date }))}
              />
            ) : (
              <div className={aa.journalSessionGroup}>
                {sessions.map((sess, index) => (
                  <JournalSessionCard
                    key={sess.id}
                    sess={sess}
                    isLast={index === sessions.length - 1}
                    onOpen={() => selectSession(sess.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </HubTabScreen>
        {absenceSheet}
      </>
    );
  }

  return (
    <div className={md.desktopJournal}>
      {offlineBanner}
      <div
        className={s.overviewHeroActions}
        style={{ marginBottom: 16, flexWrap: "wrap" }}
      >
        <Field label="Дата">
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
        <Field label={L.group}>
          <Select
            value={groupFilter}
            onChange={(e) => setGroupFilterAndUrl(e.target.value)}
          >
            <option value="">Все группы</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </Select>
        </Field>
        <Button type="button" variant="secondary" onClick={loadSessions}>
          Обновить
        </Button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1.4fr",
          gap: 16,
        }}
      >
        <Card>
          <h3 style={{ marginTop: 0 }}>
            {L.sessions} · {date}
          </h3>
          {loading ? (
            <p>Загрузка…</p>
          ) : sessions.length === 0 ? (
            <EmptyState title="Нет занятий" />
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {sessions.map((sess) => (
                <li key={sess.id} style={{ marginBottom: 8 }}>
                  <button
                    type="button"
                    onClick={() => selectSession(sess.id)}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: 12,
                      borderRadius: 10,
                      border:
                        activeSessionId === sess.id
                          ? "2px solid var(--color-primary)"
                          : "1px solid var(--color-border)",
                      background: "var(--color-surface)",
                      cursor: "pointer",
                    }}
                  >
                    <strong>{sessionLabel(sess)}</strong>
                    {sess.status === "completed" ? (
                      <span style={{ marginLeft: 8, fontSize: 12 }}>
                        ✓ завершено
                      </span>
                    ) : null}
                    <div
                      style={{
                        color: "var(--color-text-muted)",
                        fontSize: 14,
                      }}
                    >
                      {formatTimeRu(sess.start_datetime)} —{" "}
                      {formatTimeRu(sess.end_datetime)}
                      {sess.branch?.name ? ` · ${sess.branch.name}` : ""}
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: "var(--color-text-muted)",
                      }}
                    >
                      {sess.marked_count ?? sess.present_count ?? 0}/
                      {sess.enrolled_count ?? 0} отмечено
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <h3 style={{ margin: 0 }}>{L.journal}</h3>
          {!journal ? (
            <EmptyState title="Выберите занятие слева" />
          ) : (
            <>
              <p style={{ color: "var(--color-text-secondary)" }}>
                {sessionLabel(journal)}
                {journal.branch?.name ? ` · ${journal.branch.name}` : ""} ·{" "}
                {journal.trainer?.user?.full_name || "Тренер"}
              </p>
              {!canMarkSessionAttendance(journal) ? (
                <p className={s.docSectionHint}>
                  Занятие отменено — отметка недоступна
                </p>
              ) : (
                <p className={s.docSectionHint}>
                  Тап — «Был». Свайп влево — «Не был» (с причиной). Долгое
                  нажатие — «Предупредил». Списание с абонемента при «Был».
                </p>
              )}
              <AttendanceRoster
                journal={journal}
                mark={mark}
                markDisabled={markDisabled}
                onAbsence={setAbsenceModal}
              />
              <p style={{ marginTop: 16 }}>
                <Link to={hubClientsPath(hubBase)}>Все клиенты →</Link>
              </p>
            </>
          )}
        </Card>
      </div>

      {absenceSheet}
    </div>
  );
}
